import React, { useState, useEffect } from 'react';
import { useAuth, parseJwt } from './AuthContext';
import { userApi, authApi, fileApi, S3_BASE_URL } from './api';
import { openPrimeModal } from './PrimeModal';

// ─── Icons ───
const IconArrowLeft = ({ size = 20 }) => (<svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="19" y1="12" x2="5" y2="12" /><polyline points="12 19 5 12 12 5" /></svg>);
const IconChevronRight = ({ size = 18 }) => (<svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6" /></svg>);
const IconLogout = ({ size = 20 }) => (<svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" /><polyline points="16 17 21 12 16 7" /><line x1="21" y1="12" x2="9" y2="12" /></svg>);
const IconTrash = ({ size = 20 }) => (<svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6" /><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /></svg>);
const IconEdit = ({ size = 20 }) => (<svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" /></svg>);
const IconSave = ({ size = 20 }) => (<svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" /><polyline points="17 21 17 13 7 13 7 21" /><polyline points="7 3 7 8 15 8" /></svg>);

const GENDER_LABELS = { MALE: 'Male', FEMALE: 'Female', ANY: 'Other' };
const INTENT_LABELS = { FLATPGSEARCH: 'Looking for Flat/PG', FLATOWNER: 'Flat Owner', PGOWNER: 'PG Owner' };
const PREFERENCE_LABELS = {
    VEG: '🥬 Veg', NON_VEG: '🍗 Non-Veg', NON_SMOKER: '🚭 Non-Smoker', NON_DRINKER: '🚫 Non-Drinker',
    PET_LOVER: '🐾 Pet Lover', NO_PETS: '🚷 No Pets', WORK_FROM_HOME: '💻 WFH',
    EARLY_SLEEPER: '🌙 Early Sleeper', LATE_NIGHT_PERSON: '🦉 Night Owl', FITNESS_FREAK: '💪 Fitness Freak',
    MUSIC_LOVER: '🎵 Music Lover', CLEAN_AND_TIDY: '🧹 Clean & Tidy',
};
const ALL_PREFERENCES = Object.keys(PREFERENCE_LABELS);

const inputStyle = { width: '100%', padding: '14px 16px', borderRadius: '14px', border: '2px solid #DDE3DC', fontSize: '15px', fontWeight: '600', outline: 'none', background: 'white', color: '#1F2937', boxSizing: 'border-box' };
const fieldLabel = { display: 'block', fontSize: '12px', fontWeight: '800', color: '#64748B', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px' };

const ProfilePage = ({ onBack, onNavigate }) => {
    const { tokens, user, logout, refreshAccessToken, isListingUpload, isFlatNeedUpload, subscription, isSubscribed } = useAuth();
    const [profile, setProfile] = useState(null);
    const [loading, setLoading] = useState(true);
    const [editMode, setEditMode] = useState(false);
    const [saving, setSaving] = useState(false);
    const [uploadingPic, setUploadingPic] = useState(false);

    // Edit form
    const [form, setForm] = useState({
        name: '', gender: 'MALE', age: '', city: '', officeLocationName: '',
        budgetMin: '', budgetMax: '', preferences: [], intent: 'FLATPGSEARCH', profilePicUrl: ''
    });

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                const info = await userApi.getInfo(tokens.access);
                setProfile(info);
                setForm({
                    name: info.name || '',
                    gender: info.gender || 'MALE',
                    age: info.age?.toString() || '',
                    city: info.city || '',
                    officeLocationName: info.officeLocation || '',
                    budgetMin: info.budgetMin?.toString() || '',
                    budgetMax: info.budgetMax?.toString() || '',
                    preferences: info.preferences || [],
                    intent: info.intent || 'FLATPGSEARCH',
                    profilePicUrl: info.profilePicUrl || '',
                });
            } catch (err) {
                console.error('Profile load error', err);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [tokens.access]);

    const handleFileChange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setUploadingPic(true);
        try {
            const { key } = await fileApi.uploadFile(file, tokens.access);
            setForm({ ...form, profilePicUrl: key });
        } catch (err) {
            alert('Failed to upload image: ' + (err.message || ''));
        } finally {
            setUploadingPic(false);
        }
    };

    const handleSave = async () => {
        if (!form.name || !form.age || !form.city) {
            alert('Please fill all required fields');
            return;
        }
        setSaving(true);
        try {
            await userApi.updateEntity({
                name: form.name,
                gender: form.gender,
                age: parseInt(form.age),
                city: form.city,
                officeLocation: form.officeLocationName ? { name: form.officeLocationName, placeId: '', latitude: 0, longitude: 0 } : null,
                budgetMin: parseInt(form.budgetMin) || null,
                budgetMax: parseInt(form.budgetMax) || null,
                preferences: form.preferences,
                intent: form.intent,
                profilePicUrl: form.profilePicUrl,
            }, tokens.access);
            await refreshAccessToken();
            const updated = await userApi.getInfo(tokens.access);
            setProfile(updated);
            setEditMode(false);
        } catch (err) {
            alert('Failed to update profile: ' + (err.message || ''));
        } finally {
            setSaving(false);
        }
    };

    const handleLogout = () => {
        if (window.confirm('Are you sure you want to sign out?')) {
            logout();
            onBack();
        }
    };

    const handleDeleteAccount = async () => {
        if (!window.confirm('⚠️ This action is irreversible. All your data, listings, and messages will be permanently deleted. Are you sure?')) return;
        try {
            await authApi.deleteMe(tokens.access);
            logout();
            onBack();
        } catch (err) {
            alert('Failed to delete account');
        }
    };

    if (loading) {
        return (
            <div style={{ minHeight: '100vh', background: '#F3F4F6', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <div style={{ fontSize: '18px', fontWeight: '600', color: '#9CA3AF' }}>Loading profile...</div>
            </div>
        );
    }

    const ActionRow = ({ icon, label, count, onClick }) => (
        <button onClick={onClick} style={{ width: '100%', display: 'flex', alignItems: 'center', gap: '14px', padding: '16px 20px', background: 'white', border: 'none', borderBottom: '1px solid #E5E9E3', cursor: 'pointer', transition: 'background 0.15s', textAlign: 'left' }} onMouseOver={e => e.currentTarget.style.background = '#F8FAFC'} onMouseOut={e => e.currentTarget.style.background = 'white'}>
            <div style={{ fontSize: '20px' }}>{icon}</div>
            <div style={{ flex: 1 }}>
                <div style={{ fontSize: '15px', fontWeight: '700', color: '#1F2937' }}>{label}</div>
            </div>
            {count !== undefined && <span style={{ background: '#EFF6FF', color: '#2563EB', padding: '4px 10px', borderRadius: '10px', fontSize: '12px', fontWeight: '800' }}>{count}</span>}
            <IconChevronRight size={16} color="#9CA3AF" />
        </button>
    );

    return (
        <div style={{ minHeight: '100vh', background: '#F3F4F6' }}>
            <style>{`
                .profile-form-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
                @media (max-width: 480px) { .profile-form-grid { grid-template-columns: 1fr; } }
            `}</style>
            {/* Header */}
            <header style={{ background: 'linear-gradient(155deg, #1E40AF 0%, #2563EB 55%, #3B82F6 100%)', padding: '100px 20px 80px', color: 'white', position: 'relative', overflow: 'hidden' }}>
                {/* Decorative orbs */}
                <div style={{ position: 'absolute', top: '-60px', right: '-60px', width: '300px', height: '300px', background: 'rgba(255,255,255,0.05)', borderRadius: '50%', pointerEvents: 'none' }} />
                <div style={{ position: 'absolute', bottom: '-40px', left: '-40px', width: '200px', height: '200px', background: 'rgba(255,255,255,0.04)', borderRadius: '50%', pointerEvents: 'none' }} />

                <div style={{ maxWidth: '800px', margin: '0 auto', display: 'flex', alignItems: 'center', gap: '16px', position: 'relative', zIndex: 1 }}>
                    <button onClick={onBack} style={{ background: 'rgba(255,255,255,0.18)', border: '1px solid rgba(255,255,255,0.3)', borderRadius: '14px', width: '44px', height: '44px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: 'white', backdropFilter: 'blur(10px)', transition: 'background 0.2s', flexShrink: 0 }}
                        onMouseOver={e => e.currentTarget.style.background = 'rgba(255,255,255,0.3)'}
                        onMouseOut={e => e.currentTarget.style.background = 'rgba(255,255,255,0.18)'}>
                        <IconArrowLeft />
                    </button>
                    <h2 style={{ margin: 0, fontSize: '26px', fontWeight: '800', flex: 1, color: 'white', letterSpacing: '-0.5px' }}>Profile</h2>
                    {!editMode && (
                        <button onClick={() => setEditMode(true)} style={{ background: 'rgba(255,255,255,0.18)', border: '1px solid rgba(255,255,255,0.3)', borderRadius: '16px', padding: '10px 20px', color: 'white', fontWeight: '700', fontSize: '14px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', backdropFilter: 'blur(10px)', transition: 'background 0.2s' }}
                            onMouseOver={e => e.currentTarget.style.background = 'rgba(255,255,255,0.3)'}
                            onMouseOut={e => e.currentTarget.style.background = 'rgba(255,255,255,0.18)'}>
                            <IconEdit size={16} /> Edit
                        </button>
                    )}
                </div>
            </header>

            {/* Profile card overlapping header */}
            <div style={{ maxWidth: '800px', margin: '-64px auto 0', padding: '0 24px 32px', position: 'relative', zIndex: 10 }}>
                {/* Avatar + Name Card */}
                <div style={{ background: 'white', borderRadius: '24px', padding: '32px', boxShadow: '0 10px 40px rgba(0,0,0,0.06)', border: '1px solid #E5E9E3', marginBottom: '24px', textAlign: 'center' }}>
                    <div style={{ position: 'relative', display: 'inline-block', marginBottom: '16px' }}>
                        {(editMode ? form.profilePicUrl : profile?.profilePicUrl) ? (
                            <img src={`${S3_BASE_URL}${editMode ? form.profilePicUrl : profile.profilePicUrl}`} alt="Profile" style={{ width: '100px', height: '100px', borderRadius: '50%', objectFit: 'cover', border: '4px solid white', boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }} />
                        ) : (
                            <div style={{ width: '100px', height: '100px', borderRadius: '50%', background: 'linear-gradient(135deg, #3B82F6, #6366F1)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: '36px', fontWeight: '800', border: '4px solid white', boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }}>
                                {(profile?.name || 'U').charAt(0).toUpperCase()}
                            </div>
                        )}
                        {editMode && (
                            <label style={{ position: 'absolute', bottom: '0', right: '0', width: '32px', height: '32px', borderRadius: '50%', background: '#3B82F6', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', border: '2px solid white', boxShadow: '0 2px 8px rgba(0,0,0,0.15)' }}>
                                <span style={{ color: 'white', fontSize: '14px' }}>📷</span>
                                <input type="file" accept="image/*" onChange={handleFileChange} style={{ display: 'none' }} />
                            </label>
                        )}
                    </div>
                    {uploadingPic && <div style={{ fontSize: '13px', color: '#3B82F6', fontWeight: '600', marginBottom: '8px' }}>Uploading photo...</div>}
                    <h2 style={{ margin: '0 0 4px', fontSize: '24px', fontWeight: '800', color: '#111827' }}>{profile?.name || 'User'}</h2>
                    <p style={{ margin: 0, fontSize: '14px', color: '#6B7280', fontWeight: '600' }}>{profile?.phoneNumber ? `+91 ${profile.phoneNumber}` : 'Flatloop User'}</p>
                    {profile?.city && <p style={{ margin: '4px 0 0', fontSize: '14px', color: '#9CA3AF' }}>📍 {profile.city}</p>}
                </div>

                {/* ──── FlatLoop Prime ──── */}
                <div
                    onClick={() => !isSubscribed && openPrimeModal('Unlock contact numbers and unlimited chat.')}
                    style={{
                        background: isSubscribed
                            ? 'linear-gradient(135deg, #065F46, #059669)'
                            : 'linear-gradient(135deg, #1E40AF, #2563EB)',
                        borderRadius: '24px', padding: '24px', marginBottom: '24px', color: 'white',
                        cursor: isSubscribed ? 'default' : 'pointer',
                        boxShadow: '0 10px 30px rgba(37,99,235,0.25)',
                        display: 'flex', alignItems: 'center', gap: '18px',
                    }}
                >
                    <div style={{ fontSize: '30px', lineHeight: 1 }}>{isSubscribed ? '\u2b50' : '\ud83d\udd12'}</div>
                    <div style={{ flex: 1 }}>
                        <div style={{ fontSize: '18px', fontWeight: 800, marginBottom: '4px' }}>
                            {isSubscribed ? 'FlatLoop Prime is active' : 'Get FlatLoop Prime'}
                        </div>
                        <div style={{ fontSize: '14px', opacity: 0.9, lineHeight: 1.5 }}>
                            {isSubscribed
                                ? (subscription?.daysRemaining != null
                                    ? `${subscription.daysRemaining} day${subscription.daysRemaining === 1 ? '' : 's'} remaining`
                                    : 'Contact numbers and chat are unlocked')
                                : `Contact numbers + unlimited chat for \u20b9${subscription?.priceInr ?? 99}/month`}
                        </div>
                    </div>
                    {!isSubscribed && <IconChevronRight size={22} />}
                </div>

                {editMode ? (
                    /* ──── Edit Form ──── */
                    <div style={{ background: 'white', borderRadius: '24px', padding: '32px', boxShadow: '0 4px 20px rgba(0,0,0,0.04)', border: '1px solid #E5E9E3', marginBottom: '24px' }}>
                        <h3 style={{ margin: '0 0 24px', fontSize: '20px', fontWeight: '800', color: '#111827' }}>Edit Profile</h3>

                        <div style={{ marginBottom: '20px' }}>
                            <label style={fieldLabel}>Full Name *</label>
                            <input type="text" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Enter your name" style={inputStyle} />
                        </div>

                        <div className="profile-form-grid" style={{ marginBottom: '20px' }}>
                            <div>
                                <label style={fieldLabel}>Age *</label>
                                <input type="number" value={form.age} onChange={e => setForm({ ...form, age: e.target.value })} placeholder="Years" style={inputStyle} />
                            </div>
                            <div>
                                <label style={fieldLabel}>Gender *</label>
                                <select value={form.gender} onChange={e => setForm({ ...form, gender: e.target.value })} style={inputStyle}>
                                    <option value="MALE">Male</option>
                                    <option value="FEMALE">Female</option>
                                    <option value="ANY">Other</option>
                                </select>
                            </div>
                        </div>

                        <div style={{ marginBottom: '20px' }}>
                            <label style={fieldLabel}>City *</label>
                            <input type="text" value={form.city} onChange={e => setForm({ ...form, city: e.target.value })} placeholder="e.g. Bangalore" style={inputStyle} />
                        </div>

                        <div style={{ marginBottom: '20px' }}>
                            <label style={fieldLabel}>Office Location</label>
                            <input type="text" value={form.officeLocationName} onChange={e => setForm({ ...form, officeLocationName: e.target.value })} placeholder="Where do you work?" style={inputStyle} />
                        </div>

                        <div className="profile-form-grid" style={{ marginBottom: '20px' }}>
                            <div>
                                <label style={fieldLabel}>Min Budget</label>
                                <input type="number" value={form.budgetMin} onChange={e => setForm({ ...form, budgetMin: e.target.value })} placeholder="₹" style={inputStyle} />
                            </div>
                            <div>
                                <label style={fieldLabel}>Max Budget</label>
                                <input type="number" value={form.budgetMax} onChange={e => setForm({ ...form, budgetMax: e.target.value })} placeholder="₹" style={inputStyle} />
                            </div>
                        </div>

                        <div style={{ marginBottom: '20px' }}>
                            <label style={fieldLabel}>Preferences</label>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                                {ALL_PREFERENCES.map(pref => {
                                    const isSelected = form.preferences.includes(pref);
                                    return (
                                        <div key={pref} onClick={() => setForm({ ...form, preferences: isSelected ? form.preferences.filter(p => p !== pref) : [...form.preferences, pref] })}
                                            style={{ padding: '8px 14px', borderRadius: '20px', fontSize: '13px', fontWeight: '600', cursor: 'pointer', transition: 'all 0.2s', border: `1.5px solid ${isSelected ? '#3B82F6' : '#DDE3DC'}`, background: isSelected ? '#EFF6FF' : 'white', color: isSelected ? '#2563EB' : '#6B7280', userSelect: 'none' }}>
                                            {PREFERENCE_LABELS[pref]}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>

                        <div style={{ marginBottom: '24px' }}>
                            <label style={fieldLabel}>Looking for</label>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                {[
                                    { value: 'FLATPGSEARCH', label: 'Looking for Flat/PG' },
                                    { value: 'FLATOWNER', label: 'I own a Flat' },
                                    { value: 'PGOWNER', label: 'I manage a PG' },
                                ].map(opt => (
                                    <div key={opt.value} onClick={() => setForm({ ...form, intent: opt.value })} style={{ display: 'flex', alignItems: 'center', padding: '14px', border: `2px solid ${form.intent === opt.value ? '#3B82F6' : '#DDE3DC'}`, borderRadius: '14px', cursor: 'pointer', background: form.intent === opt.value ? '#EFF6FF' : 'white', transition: 'all 0.2s' }}>
                                        <div style={{ width: '20px', height: '20px', borderRadius: '50%', border: `6px solid ${form.intent === opt.value ? '#3B82F6' : '#D1D5DB'}`, marginRight: '12px' }} />
                                        <span style={{ fontSize: '14px', fontWeight: form.intent === opt.value ? '700' : '500', color: form.intent === opt.value ? '#2563EB' : '#4B5563' }}>{opt.label}</span>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div style={{ display: 'flex', gap: '12px' }}>
                            <button onClick={() => setEditMode(false)} style={{ flex: 1, padding: '14px', borderRadius: '14px', border: '2px solid #DDE3DC', background: 'white', color: '#6B7280', fontWeight: '700', fontSize: '15px', cursor: 'pointer' }}>Cancel</button>
                            <button onClick={handleSave} disabled={saving} style={{ flex: 2, padding: '14px', borderRadius: '14px', border: 'none', background: saving ? '#9CA3AF' : 'linear-gradient(135deg, #3B82F6, #2563EB)', color: 'white', fontWeight: '800', fontSize: '15px', cursor: saving ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', boxShadow: '0 8px 24px rgba(47,125,87,0.3)' }}>
                                <IconSave size={18} /> {saving ? 'Saving...' : 'Save Changes'}
                            </button>
                        </div>
                    </div>
                ) : (
                    /* ──── View Mode ──── */
                    <>
                        {/* Info Card */}
                        <div style={{ background: 'white', borderRadius: '24px', overflow: 'hidden', boxShadow: '0 4px 20px rgba(0,0,0,0.04)', border: '1px solid #E5E9E3', marginBottom: '24px' }}>
                            <div style={{ padding: '20px 24px', borderBottom: '1px solid #E5E9E3' }}>
                                <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '800', color: '#111827' }}>Personal Information</h3>
                            </div>
                            <div style={{ padding: '0' }}>
                                {[
                                    { icon: '👤', label: 'Gender', value: GENDER_LABELS[profile?.gender] },
                                    { icon: '🎂', label: 'Age', value: profile?.age ? `${profile.age} years` : null },
                                    { icon: '📍', label: 'City', value: profile?.city },
                                    { icon: '🏢', label: 'Office', value: profile?.officeLocation },
                                    { icon: '💰', label: 'Budget', value: profile?.budgetMin || profile?.budgetMax ? `₹${(profile.budgetMin || 0).toLocaleString()} - ₹${(profile.budgetMax || 0).toLocaleString()}` : null },
                                    { icon: '🎯', label: 'Intent', value: INTENT_LABELS[profile?.intent] },
                                ].map((item, i) => (
                                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '14px', padding: '14px 24px', borderBottom: '1px solid #F9FAFB' }}>
                                        <span style={{ fontSize: '18px' }}>{item.icon}</span>
                                        <div style={{ flex: 1 }}>
                                            <div style={{ fontSize: '12px', color: '#9CA3AF', fontWeight: '600' }}>{item.label}</div>
                                            <div style={{ fontSize: '15px', fontWeight: '700', color: '#1F2937', marginTop: '2px' }}>{item.value || 'Not set'}</div>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {profile?.preferences?.length > 0 && (
                                <div style={{ padding: '16px 24px', borderTop: '1px solid #E5E9E3' }}>
                                    <div style={{ fontSize: '12px', fontWeight: '800', color: '#9CA3AF', textTransform: 'uppercase', marginBottom: '10px' }}>Preferences</div>
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                                        {profile.preferences.map(p => (
                                            <span key={p} style={{ background: '#F3F4F6', padding: '6px 14px', borderRadius: '16px', fontSize: '13px', fontWeight: '700', color: '#374151', border: '1px solid #DDE3DC' }}>
                                                {PREFERENCE_LABELS[p] || p}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Quick Actions — only show entries that match JWT claims */}
                        {(isListingUpload || isFlatNeedUpload) && (
                            <div style={{ background: 'white', borderRadius: '24px', overflow: 'hidden', boxShadow: '0 4px 20px rgba(0,0,0,0.04)', border: '1px solid #E5E9E3', marginBottom: '24px' }}>
                                <div style={{ padding: '20px 24px', borderBottom: '1px solid #E5E9E3' }}>
                                    <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '800', color: '#111827' }}>My Activity</h3>
                                </div>
                                {isListingUpload && <ActionRow icon="🏠" label="My Listings" onClick={() => onNavigate('myListings')} />}
                                {isFlatNeedUpload && <ActionRow icon="📣" label="My Requirement" onClick={() => onNavigate('myRequirement')} />}
                            </div>
                        )}

                        {/* Danger Zone */}
                        <div style={{ background: 'white', borderRadius: '24px', overflow: 'hidden', boxShadow: '0 4px 20px rgba(0,0,0,0.04)', border: '1px solid #E5E9E3' }}>
                            <button onClick={handleLogout} style={{ width: '100%', display: 'flex', alignItems: 'center', gap: '14px', padding: '16px 24px', background: 'white', border: 'none', borderBottom: '1px solid #E5E9E3', cursor: 'pointer', textAlign: 'left' }} onMouseOver={e => e.currentTarget.style.background = '#F8FAFC'} onMouseOut={e => e.currentTarget.style.background = 'white'}>
                                <div style={{ width: '40px', height: '40px', borderRadius: '12px', background: '#FEF2F2', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#DC2626' }}><IconLogout size={20} /></div>
                                <span style={{ fontSize: '15px', fontWeight: '700', color: '#DC2626' }}>Sign Out</span>
                            </button>
                            <button onClick={handleDeleteAccount} style={{ width: '100%', display: 'flex', alignItems: 'center', gap: '14px', padding: '16px 24px', background: 'white', border: 'none', cursor: 'pointer', textAlign: 'left' }} onMouseOver={e => e.currentTarget.style.background = '#FEF2F2'} onMouseOut={e => e.currentTarget.style.background = 'white'}>
                                <div style={{ width: '40px', height: '40px', borderRadius: '12px', background: '#FEE2E2', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#DC2626' }}><IconTrash size={18} /></div>
                                <span style={{ fontSize: '15px', fontWeight: '700', color: '#DC2626' }}>Delete Account</span>
                            </button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default ProfilePage;
